import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { isPlanRequired } from "../lib/analytics";
import { isFreeQuotaExceeded } from "../lib/upsell";
import { StandardPaywallDialog } from "../components/StandardUpsell";
import Layout from "../components/Layout";
import { Upload, FileText, X, ArrowRight, Sparkle } from "@phosphor-icons/react";

const PLACEHOLDER_TITLE = 'Es. "Appartamento centro città - RGE 145/2025"';

export default function NewAnalysis() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [auction, setAuction] = useState(null);
    const [perizia, setPerizia] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [paywall, setPaywall] = useState(null);

    const [form, setForm] = useState({
        title: "",
        province: "",
        auction_url: "",
        address: "",
        comune: "",
        tribunale: "",
        base_price: "",
        min_offer: "",
        surface_sqm: "",
        rooms: "",
        floor: "",
        occupied: "",
        notes: "",
    });

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const createAuction = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const payload = {
                title: form.title,
                province: form.province,
                auction_url: form.auction_url || null,
                address: form.address || null,
                comune: form.comune || null,
                tribunale: form.tribunale || null,
                base_price: form.base_price ? parseFloat(form.base_price) : null,
                min_offer: form.min_offer ? parseFloat(form.min_offer) : null,
                surface_sqm: form.surface_sqm ? parseFloat(form.surface_sqm) : null,
                rooms: form.rooms ? parseInt(form.rooms, 10) : null,
                floor: form.floor || null,
                occupied:
                    form.occupied === "yes" ? true : form.occupied === "no" ? false : null,
                notes: form.notes || null,
            };
            const { data } = await api.post("/auctions", payload);
            setAuction(data);
            setStep(2);
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    const uploadFile = async (file, kind) => {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        const { data } = await api.post(`/auctions/${auction.id}/documents`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
    };

    const startAnalysis = async () => {
        setError("");
        setSubmitting(true);
        try {
            // Upload pending files first
            if (perizia && !perizia.uploaded) {
                await uploadFile(perizia.file, "perizia");
                setPerizia({ ...perizia, uploaded: true });
            }
            if (avviso && !avviso.uploaded) {
                await uploadFile(avviso.file, "avviso");
                setAvviso({ ...avviso, uploaded: true });
            }
            await api.post("/analyses/start", { auction_id: auction.id });
            navigate(`/auctions/${auction.id}`);
        } catch (e2) {
            if (isPlanRequired(e2)) {
                setPaywall({ variant: "plan_required", source: "plan_required", returnTo: `/auctions/${auction.id}` });
            } else if (isFreeQuotaExceeded(e2)) {
                setPaywall({ variant: "second_analysis_paywall", source: "second_analysis_paywall", returnTo: `/auctions/${auction.id}` });
            } else {
                setError(formatApiError(e2.response?.data?.detail) || e2.message);
            }
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="mb-8" data-testid="new-analysis-page">
                <div className="overline mb-2">Nuova analisi</div>
                <h1 className="font-display-black text-4xl leading-none">
                    Crea una due diligence.
                </h1>
                <p className="text-sm text-gray-600 mt-3 max-w-xl">
                    Step 1: dati dell&apos;asta. Step 2: carica i documenti. Step 3: il
                    sistema esegue 7 agenti AI e genera il report.
                </p>
            </div>

            <Stepper step={step} />

            {error && (
                <div
                    data-testid="form-error"
                    className="border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3 mb-6"
                >
                    {error}
                </div>
            )}
            <StandardPaywallDialog open={!!paywall} onClose={() => setPaywall(null)}
                                   variant={paywall?.variant} source={paywall?.source} returnTo={paywall?.returnTo} />

            {step === 1 && (
                <form
                    data-testid="auction-form"
                    onSubmit={createAuction}
                    className="bg-white border border-gray-200 p-6 md:p-8 grid md:grid-cols-2 gap-6"
                >
                    <Field label="Titolo identificativo" required full>
                        <input
                            data-testid="title-input"
                            required
                            value={form.title}
                            onChange={upd("title")}
                            placeholder={PLACEHOLDER_TITLE}
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <Field label="Provincia" required>
                        <input
                            data-testid="province-input"
                            value={form.province}
                            onChange={upd("province")}
                            placeholder="Es. Milano"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <Field label="URL asta (opzionale)">
                        <input
                            data-testid="url-input"
                            value={form.auction_url}
                            onChange={upd("auction_url")}
                            placeholder="https://pvp.giustizia.it/..."
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <Field label="Indirizzo">
                        <input
                            data-testid="address-input"
                            value={form.address}
                            onChange={upd("address")}
                            placeholder="Via Roma 15"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <Field label="Comune">
                        <input
                            data-testid="comune-input"
                            value={form.comune}
                            onChange={upd("comune")}
                            placeholder="Es. Milano"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <Field label="Tribunale">
                        <input
                            data-testid="tribunale-input"
                            value={form.tribunale}
                            onChange={upd("tribunale")}
                            placeholder="Es. Tribunale di Milano"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <Field label="Prezzo base €">
                        <input
                            data-testid="base-price-input"
                            type="number"
                            step="100"
                            value={form.base_price}
                            onChange={upd("base_price")}
                            placeholder="150000"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none font-mono-tight"
                        />
                    </Field>

                    <Field label="Offerta minima €">
                        <input
                            data-testid="min-offer-input"
                            type="number"
                            step="100"
                            value={form.min_offer}
                            onChange={upd("min_offer")}
                            placeholder="112500"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none font-mono-tight"
                        />
                    </Field>

                    <Field label="Superficie m²">
                        <input
                            data-testid="surface-input"
                            type="number"
                            step="1"
                            value={form.surface_sqm}
                            onChange={upd("surface_sqm")}
                            placeholder="85"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none font-mono-tight"
                        />
                    </Field>

                    <Field label="Locali">
                        <input
                            data-testid="rooms-input"
                            type="number"
                            value={form.rooms}
                            onChange={upd("rooms")}
                            placeholder="3"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none font-mono-tight"
                        />
                    </Field>

                    <Field label="Piano">
                        <input
                            data-testid="floor-input"
                            value={form.floor}
                            onChange={upd("floor")}
                            placeholder="Secondo"
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <Field label="Occupazione">
                        <select
                            data-testid="occupied-input"
                            value={form.occupied}
                            onChange={upd("occupied")}
                            className="w-full h-11 px-3 border border-gray-300 text-sm focus:border-black focus:outline-none bg-white"
                        >
                            <option value="">— Da estrarre dai documenti —</option>
                            <option value="no">Libero</option>
                            <option value="yes">Occupato</option>
                        </select>
                    </Field>

                    <Field label="Note aggiuntive" full>
                        <textarea
                            data-testid="notes-input"
                            rows={3}
                            value={form.notes}
                            onChange={upd("notes")}
                            placeholder="Eventuali contesti aggiuntivi…"
                            className="w-full px-3 py-2 border border-gray-300 text-sm focus:border-black focus:outline-none"
                        />
                    </Field>

                    <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            data-testid="create-auction-button"
                            disabled={submitting}
                            className="h-12 px-6 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40 flex items-center gap-2"
                        >
                            Continua
                            <ArrowRight size={14} weight="bold" />
                        </button>
                    </div>
                </form>
            )}

            {step === 2 && (
                <div className="space-y-6" data-testid="upload-step">
                    <div className="bg-white border border-gray-200 p-6 md:p-8">
                        <div className="overline mb-2">Step 2 / Carica documenti</div>
                        <h2 className="font-display text-2xl font-bold mb-2">
                            Carica perizia e avviso di vendita
                        </h2>
                        <p className="text-sm text-gray-600 mb-6">
                            Formato PDF, max 25 MB. Almeno un documento è necessario per
                            avviare l&apos;analisi.
                        </p>

                        <div className="grid md:grid-cols-2 gap-4">
                            <FileSlot
                                label="Perizia tecnica"
                                kind="perizia"
                                file={perizia}
                                onSelect={(f) => setPerizia({ file: f })}
                                onClear={() => setPerizia(null)}
                                testid="upload-perizia"
                            />
                            <FileSlot
                                label="Avviso di vendita"
                                kind="avviso"
                                file={avviso}
                                onSelect={(f) => setAvviso({ file: f })}
                                onClear={() => setAvviso(null)}
                                testid="upload-avviso"
                            />
                        </div>
                    </div>

                    <div className="flex justify-between items-center">
                        <button
                            onClick={() => setStep(1)}
                            data-testid="back-to-step1"
                            className="h-11 px-4 border border-gray-300 hover:border-black text-sm"
                        >
                            ← Indietro
                        </button>
                        <button
                            onClick={startAnalysis}
                            disabled={submitting || (!perizia && !avviso)}
                            data-testid="start-analysis-button"
                            className="h-12 px-6 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40 flex items-center gap-2"
                        >
                            <Sparkle size={14} weight="bold" />
                            {submitting ? "Avvio analisi…" : "Avvia analisi multi-agente"}
                        </button>
                    </div>
                </div>
            )}
        </Layout>
    );
}

function Stepper({ step }) {
    const items = [
        { n: 1, label: "Dati asta" },
        { n: 2, label: "Documenti" },
        { n: 3, label: "Analisi AI" },
    ];
    return (
        <div className="flex items-center gap-2 mb-6">
            {items.map((it, i) => (
                <div key={it.n} className="flex items-center gap-2">
                    <div
                        className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${
                            step >= it.n ? "bg-black text-white" : "bg-gray-100 text-gray-400 border border-gray-200"
                        }`}
                    >
                        {it.n}
                    </div>
                    <div className={`text-xs font-bold uppercase tracking-wider ${step >= it.n ? "text-black" : "text-gray-400"}`}>
                        {it.label}
                    </div>
                    {i < items.length - 1 && <div className="w-8 h-px bg-gray-300 mx-2" />}
                </div>
            ))}
        </div>
    );
}

function Field({ label, required, full, children }) {
    return (
        <label className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="overline block mb-2">
                {label} {required && <span className="text-red-600">*</span>}
            </span>
            {children}
        </label>
    );
}

function FileSlot({ label, file, onSelect, onClear, testid }) {
    return (
        <div className="border border-gray-300 p-5 bg-white/[0.03]" data-testid={testid}>
            <div className="overline mb-3">{label}</div>
            {!file ? (
                <label
                    className="flex flex-col items-center justify-center h-32 border border-dashed border-gray-400 hover:border-black hover:bg-white transition-colors cursor-pointer"
                    data-testid={`${testid}-dropzone`}
                >
                    <Upload size={24} className="text-gray-500 mb-2" />
                    <span className="text-xs text-gray-600 font-medium">Seleziona PDF</span>
                    <input
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        data-testid={`${testid}-input`}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) onSelect(f);
                        }}
                    />
                </label>
            ) : (
                <div className="flex items-center justify-between bg-white border border-gray-300 p-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <FileText size={18} weight="bold" />
                        <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{file.file.name}</div>
                            <div className="text-[10px] text-gray-500 font-mono-tight">
                                {(file.file.size / 1024).toFixed(0)} KB
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClear}
                        data-testid={`${testid}-clear`}
                        className="w-7 h-7 flex items-center justify-center hover:bg-gray-100"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
        </div>
    );
}
